import { useCallback, useLayoutEffect, useRef } from "react";
import type { BrowserAuthSession } from "@/services/browserAuthSession";
interface Generation {
  identity: string;
  controller: AbortController;
  session: BrowserAuthSession;
}
export function useBrowserAuthSession(
  identity: string,
  token: () => Promise<string | null>,
): { session: BrowserAuthSession; invalidate: () => void } {
  const current = useRef<Generation | null>(null),
    mounted = useRef(true),
    provider = useRef(token);
  provider.current = token;
  if (!current.current || current.current.identity !== identity) {
    current.current?.controller.abort();
    const controller = new AbortController();
    const generation: Generation = {
      identity,
      controller,
      session: {
        key: crypto.randomUUID(),
        signal: controller.signal,
        isCurrent: () =>
          mounted.current &&
          current.current === generation &&
          !controller.signal.aborted,
        getToken: async () => {
          const active = () => {
            if (!generation.session.isCurrent())
              throw new DOMException(
                "Authentication context changed",
                "AbortError",
              );
          };
          active();
          const capturedProvider = provider.current;
          let onAbort = () => {};
          const interrupted = new Promise<never>((_resolve, reject) => {
            onAbort = () =>
              reject(
                new DOMException(
                  "Authentication context changed",
                  "AbortError",
                ),
              );
            controller.signal.addEventListener("abort", onAbort, {
              once: true,
            });
          });
          try {
            const value = await Promise.race([capturedProvider(), interrupted]);
            active();
            return value;
          } finally {
            controller.signal.removeEventListener("abort", onAbort);
          }
        },
      },
    };
    current.current = generation;
  }
  // Restore liveness before child query subscriptions replay in StrictMode.
  useLayoutEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      const previous = current.current;
      queueMicrotask(() => {
        if (!mounted.current) previous?.controller.abort();
      });
    };
  }, []);
  const generation = current.current;
  const invalidate = useCallback(
    () => generation.controller.abort(),
    [generation],
  );
  return { session: generation.session, invalidate };
}
